import { db, functions } from "../firebase/config";
import {
  doc,
  getDoc,
  Timestamp,
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

/**
 * Interface matching the UsernameData structure from Cloud Functions
 */
export interface UsernameData {
  userId: string;
  username: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Interface for the following collection document
 */
export interface FollowingData {
  followingList: string[]; // Array of user IDs being followed
  updatedAt: Timestamp;
}

/**
 * Interface for the followers collection document
 * This collection is automatically maintained by Cloud Functions
 */
export interface FollowersData {
  followersList: string[]; // Array of user IDs who follow this user
  updatedAt: Timestamp;
}

/**
 * Interface for a comment on a post
 */
export interface Comment {
  commentId: string;
  userId: string;
  text: string;
  timestamp: Timestamp;
}

export interface Post {
  userID: string;
  username: string;
  userImageUrl: string;
  postTimestamp: Timestamp;
  notes: string;
  duration: number;
  mediaList: string[];
  load?: number;
  AverageHR?: number;
  trainingLogID?: string;
  activityLogID?: string;
  performanceLogID?: string;
  sessionID?: string;
  isPrivate?: boolean; // Whether post is private (visible only to author)
  gpsDataIsPrivate?: boolean; // Whether GPS/route data is private (hidden from others)
  imagePosition?: { x: number; y: number }; // Image crop position (percentage)
  likesList?: string[]; // Array of userIds who liked
  CommentsList?: Comment[]; // Array of comment objects
}

/**
 * Look up a user by their username
 * @param username The username to search for (case-insensitive)
 * @returns UsernameData if found, null otherwise
 */
export const lookupUsername = async (
  username: string,
): Promise<UsernameData | null> => {
  try {
    const usernameDoc = await getDoc(
      doc(db, "usernames", username.toLowerCase()),
    );

    if (!usernameDoc.exists()) {
      return null;
    }

    return usernameDoc.data() as UsernameData;
  } catch (error) {
    console.error("Error looking up username:", error);
    throw error;
  }
};

/**
 * Search for usernames by prefix or exact match
 * @param searchTerm The search term to look for
 * @param maxResults Maximum number of results to return (default 50)
 * @returns Array of matching UsernameData
 */
export const searchUsernames = async (
  searchTerm: string,
  maxResults: number = 50,
): Promise<UsernameData[]> => {
  try {
    const searchLower = searchTerm.toLowerCase().trim();

    if (!searchLower) {
      return [];
    }

    // Search using prefix matching (startsWith)
    const usernamesRef = collection(db, "usernames");
    const q = query(
      usernamesRef,
      where("username", ">=", searchLower),
      where("username", "<=", searchLower + "\uf8ff"),
      orderBy("username"),
      limit(maxResults),
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as UsernameData);
  } catch (error) {
    console.error("Error searching usernames:", error);
    throw error;
  }
};

/**
 * Get the list of users that a user is following
 * @param userId The ID of the user whose following list to retrieve
 * @returns Array of user IDs being followed
 */
export const getFollowingList = async (userId: string): Promise<string[]> => {
  try {
    const followingDoc = await getDoc(doc(db, "following", userId));

    if (!followingDoc.exists()) {
      return [];
    }

    const data = followingDoc.data() as FollowingData;
    return data.followingList || [];
  } catch (error) {
    console.error("Error getting following list:", error);
    throw error;
  }
};

interface FollowCallableRequest {
  followingUserID: string;
}

interface FollowCallableResponse {
  success: boolean;
  alreadyFollowing?: boolean;
  notFollowing?: boolean;
}

const followUserCallable = httpsCallable<
  FollowCallableRequest,
  FollowCallableResponse
>(functions, "followUser");

const unfollowUserCallable = httpsCallable<
  FollowCallableRequest,
  FollowCallableResponse
>(functions, "unfollowUser");

/**
 * Follow a user.
 *
 * Delegates to the `followUser` Cloud Function which atomically updates
 * the caller's `following` doc, the target's `followers` doc, and seeds
 * recent posts into the caller's feed. Awaiting this resolves only once
 * all of those side effects have completed server-side, so callers can
 * immediately refresh their feed afterwards.
 *
 * @param _userId  The caller's UID. Kept for backwards-compatible call
 *                 sites — the cloud function uses request.auth.uid.
 * @param followingUserId The ID of the user to follow.
 */
export const followUser = async (
  _userId: string,
  followingUserId: string,
): Promise<void> => {
  try {
    if (_userId === followingUserId) {
      throw new Error("Cannot follow yourself");
    }
    await followUserCallable({ followingUserID: followingUserId });
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

/**
 * Unfollow a user.
 *
 * Delegates to the `unfollowUser` Cloud Function which removes the
 * relationship from `following`/`followers` and strips that user's posts
 * from the caller's feed. Awaiting this guarantees the feed is clean
 * server-side by the time it resolves.
 */
export const unfollowUser = async (
  _userId: string,
  followingUserId: string,
): Promise<void> => {
  try {
    await unfollowUserCallable({ followingUserID: followingUserId });
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
};

/**
 * Check if a user is following another user
 * @param userId The ID of the user who might be following
 * @param followingUserId The ID of the user being checked
 * @returns True if following, false otherwise
 */
export const isFollowing = async (
  userId: string,
  followingUserId: string,
): Promise<boolean> => {
  try {
    const followingList = await getFollowingList(userId);
    return followingList.includes(followingUserId);
  } catch (error) {
    console.error("Error checking following status:", error);
    throw error;
  }
};

/**
 * Get a list of users who are following a specific user
 * Reads from the followers collection which is automatically maintained by Cloud Functions
 * @param userId The ID of the user whose followers to retrieve
 * @returns Array of user IDs who are following this user
 */
export const getFollowers = async (userId: string): Promise<string[]> => {
  try {
    const followersDoc = await getDoc(doc(db, "followers", userId));

    if (!followersDoc.exists()) {
      return [];
    }

    const data = followersDoc.data() as FollowersData;
    return data.followersList || [];
  } catch (error) {
    console.error("Error getting followers:", error);
    throw error;
  }
};

/**
 * Get follower and following counts for a user
 * @param userId The ID of the user
 * @returns Object with followerCount and followingCount
 */
export const getConnectionCounts = async (
  userId: string,
): Promise<{ followerCount: number; followingCount: number }> => {
  try {
    const [followers, following] = await Promise.all([
      getFollowers(userId),
      getFollowingList(userId),
    ]);

    return {
      followerCount: followers.length,
      followingCount: following.length,
    };
  } catch (error) {
    console.error("Error getting connection counts:", error);
    throw error;
  }
};
